import { EncryptionHandler } from "./encryption-handler"

export class SecurityHandler {
  private readonly database = 'firefile'
  private readonly store = 'keys'

  private textEncoder = new TextEncoder()
  private numIterations: number = 500000 // (5-10 sec compution time)
  private derivedKeyLengthBits: number = 256 // 32 Bytes
  private ivLength: number = 16
  private saltLength: number = 16
  private randomKeyLength: number = 16

  public async encryptKey(key: string): Promise<ArrayBuffer> {
    const encryptedKey = await this.encryptWithMasterKey(key)

    return encryptedKey
  }

  public async decryptKey(encryptedKey: ArrayBuffer): Promise<string> {
    const decryptedKey = await this.decryptWithMasterKey(encryptedKey)

    return decryptedKey
  }

  public async decryptKeyString(encryptedKeyStr: string): Promise<string> {
    const keyEnc = SecurityHandler.stringToArrayBuffer(atob(encryptedKeyStr))
    return await this.decryptKey(keyEnc)
  }

  public async decryptString(str: string, key: string): Promise<string> {
    const ab = SecurityHandler.stringToArrayBuffer(atob(str))
    const iv = new Uint8Array(ab.slice(0, 16))
    const data = await new Response(ab.slice(16)).blob()

    const encryptionHandler = EncryptionHandler.getDecryptor(iv)
    await encryptionHandler.setKey(key)
    const dataDec = await encryptionHandler.decryptBlob(data)

    return String.fromCharCode.apply(null, new Uint8Array(dataDec))
  }

  public getRandomKey(): string {
    return this.generateRandomString(this.randomKeyLength)
  }

  private async encryptWithMasterKey(value: string): Promise<ArrayBuffer> {
    const salt = this.generateSalt()
    const derivedKey = await this.deriveKey(salt)
    const iv = crypto.getRandomValues(new Uint8Array(this.ivLength))

    const encoded: ArrayBuffer = await crypto.subtle.encrypt(
      {
        name: 'AES-GCM',
        iv
      },
      derivedKey,
      new TextEncoder().encode(value)
    )

    const tmp: Uint8Array = new Uint8Array(iv.byteLength + salt.byteLength + encoded.byteLength)
    tmp.set(iv, 0)
    tmp.set(salt, iv.byteLength)
    tmp.set(new Uint8Array(encoded), iv.byteLength + salt.byteLength)

    return tmp.buffer
  }

  private async decryptWithMasterKey(value: ArrayBuffer): Promise<string> {
    const salt = new Uint8Array(value.slice(this.ivLength, this.ivLength + this.saltLength))
    const derivedKey = await this.deriveKey(salt)
    const iv = new Uint8Array(value.slice(0, this.ivLength))

    const decrypted: ArrayBuffer = await crypto.subtle.decrypt(
      {
        name: 'AES-GCM',
        iv
      },
      derivedKey,
      new Uint8Array(value.slice(this.ivLength + this.saltLength))
    )

    return new TextDecoder().decode(decrypted)
  }

  private async deriveKey(salt: Uint8Array): Promise<CryptoKey> {
    const masterKey = await this.getMasterKey()
    const derivedKey = await crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt,
        iterations: this.numIterations,
        hash: 'SHA-256'
      },
      masterKey,
      {
        name: 'AES-GCM',
        length: this.derivedKeyLengthBits
      },
      true,
      ['encrypt', 'decrypt']
    )

    return derivedKey
  }

  private generateSalt(): Uint8Array {
    return crypto.getRandomValues(new Uint8Array(16))
  }

  private async saveKeys(masterKey: CryptoKey, authKey: CryptoKey): Promise<void> {
    let database: IDBDatabase = await this.openDatabase(this.database)
    await this.addObject(database, this.store, {
      key: 'masterKey',
      value: masterKey
    })
    await this.addObject(database, this.store, {
      key: 'authKey',
      value: authKey
    })

    database.close()
  }

  public async clearKeys(): Promise<void> {
    let database: IDBDatabase = await this.openDatabase(this.database);
    await this.deleteObject(database, this.store, 'masterKey');
    await this.deleteObject(database, this.store, 'authKey');

    database.close();
  }

  private async getMasterKey(): Promise<CryptoKey> {
    let database: IDBDatabase = await this.openDatabase(this.database)
    const masterKey: CryptoKey = await this.getObject(database, this.store, 'masterKey')


    return masterKey
  }

  private openDatabase(name: string): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const request: IDBOpenDBRequest = indexedDB.open(name, 1)
      request.onerror = (event: any) => reject(event)
      request.onsuccess = (event: any) => resolve(request.result)
      request.onupgradeneeded = (event: any) => {
        this.createObjectStore(request.result, this.store)
      }
    })
  }

  private createObjectStore(database: IDBDatabase, store: string) {
    if (!database.objectStoreNames.contains(store)) {
      database.createObjectStore(store)
    }
  }

  private addObject(database: IDBDatabase, store: string, object: any): Promise<void> {
    return new Promise((resolve, reject) => {
      const objectStore = database.transaction(store, 'readwrite').objectStore(store)
      const putRequest = objectStore.put(object.value, object.key)
      putRequest.onsuccess = (event: any) => resolve()
      putRequest.onerror = (event: any) => reject(event)
    })
  }

  private getObject(database: IDBDatabase, store: string, key: string): Promise<any> {
    return new Promise((resolve, reject) => {
      const objectStore = database.transaction(store, 'readonly').objectStore(store)
      const getRequest = objectStore.get(key)
      getRequest.onsuccess = (event: any) => {
        resolve(event.target.result)
      }
      getRequest.onerror = (event: any) => reject(event)
    })
  }

  private deleteObject(database: IDBDatabase, store: string, key: string): Promise<void> {
    return new Promise((resolve, reject) => {
        const objectStore = database.transaction(store, 'readwrite').objectStore(store)
        const deleteRequest = objectStore.delete(key)
        deleteRequest.onsuccess = (event: any) => {
            resolve(event.target.result)
        }
        deleteRequest.onerror = (event: any) => reject(event)
    })
  }

  public async initKeys(username: string, password: string): Promise<void> {
    const salt = this.textEncoder.encode(username)

    const importedPassword = await crypto.subtle.importKey('raw', this.textEncoder.encode(password), 'PBKDF2', false, ['deriveKey'])
    const tmpKey = await crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt,
        iterations: this.numIterations,
        hash: 'SHA-256'
      },
      importedPassword,
      {
        name: 'AES-GCM',
        length: this.derivedKeyLengthBits
      },
      true,
      ['encrypt']
    )

    const exportedTmpKey = await crypto.subtle.exportKey('raw', tmpKey)
    const importedTmpKey = await crypto.subtle.importKey('raw', exportedTmpKey, 'PBKDF2', false, ['deriveKey'])

    const masterKeyTmp = await crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt,
        iterations: this.numIterations,
        hash: 'SHA-256'
      },
      importedTmpKey,
      {
        name: 'AES-GCM',
        length: this.derivedKeyLengthBits
      },
      true,
      ['encrypt', 'decrypt']
    )
    const masterKeyExported = await crypto.subtle.exportKey('raw', masterKeyTmp)
    const importedMasterKey = await crypto.subtle.importKey('raw', masterKeyExported, 'PBKDF2', false, ['deriveKey'])

    const authKey = await crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt,
        iterations: this.numIterations,
        hash: 'SHA-256'
      },
      importedTmpKey,
      {
        name: 'AES-GCM',
        length: this.derivedKeyLengthBits
      },
      true,
      ['encrypt']
    )

    //const authentificationKey = new Uint8Array(await crypto.subtle.exportKey('raw', authKey))

    this.saveKeys(importedMasterKey, authKey)
  }

  private generateRandomString(length: number): string {
    let result = ''
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
    const charactersLength = characters.length
    for (let i = 0; i < length; i++) {
      result += characters.charAt(Math.floor(Math.random() * charactersLength))
    }
    return result
  }

  public static stringToArrayBuffer(str: string): ArrayBuffer {
    const buf = new ArrayBuffer(str.length)
    let bufView = new Uint8Array(buf)
    for (let i = 0, strLen = str.length; i < strLen; i++) {
      bufView[i] = str.charCodeAt(i)
    }
    return buf
  }
}
